import {
  resolveFeatureUseViewport,
  sanitizeFeatureUseEventName,
  stripFeatureUseProps,
} from '@utils/featureUseMetadata'

const FEATURE_USE_FETCH_TIMEOUT_MS = 8000

function getGaMeasurementId(): string | undefined {
  return import.meta.env.VITE_GOOGLE_ANALYTICS_ID
}

function getAccessToken(): string | null {
  try {
    return localStorage.getItem('token')
  } catch {
    return null
  }
}

function buildMetadata(props?: Record<string, unknown>): Record<string, string | number | boolean> | undefined {
  const base = stripFeatureUseProps(props) ?? {}
  if (!base.viewport && typeof window !== 'undefined') {
    base.viewport = resolveFeatureUseViewport(window.innerWidth)
  }
  return Object.keys(base).length > 0 ? base : undefined
}

function mirrorToGtag(eventName: string, metadata?: Record<string, string | number | boolean>): void {
  if (!getGaMeasurementId() || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return
  }

  window.gtag('event', eventName, metadata ?? {})
}

function abortSignalWithTimeout(ms: number): AbortSignal | undefined {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms)
  }
  return undefined
}

/**
 * Fire-and-forget feature_use telemetry for signed-in users.
 * Never throws; does not await network in callers (void internally).
 */
export function trackFeature(eventName: string, props?: Record<string, unknown>): void {
  try {
    const token = getAccessToken()
    if (!token) {
      return
    }

    const trimmed = sanitizeFeatureUseEventName(eventName)
    if (!trimmed) {
      return
    }

    const metadata = buildMetadata(props)

    try {
      mirrorToGtag(trimmed, metadata)
    } catch {
      // gtag failures must not block the first-party write
    }

    const signal = abortSignalWithTimeout(FEATURE_USE_FETCH_TIMEOUT_MS)

    void fetch('/api/v1/analytics/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        eventName: trimmed,
        metadata,
      }),
      keepalive: true,
      ...(signal ? { signal } : {}),
    }).catch(() => {
      // Swallow — analytics must not affect UX
    })
  } catch {
    // Never throw to callers
  }
}
